import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { AlertCircle, Check, Download, Loader2 } from 'lucide-react';
import { PdfRenderer } from '../../../../nebula/components/export/PdfRenderer';
import { usePdfExport } from '../../../../nebula/hooks/usePdfExport';
import type { VisualizationData } from '../../../../nebula/types';
import type { NebulaMessage } from '../../../../types/nebula-insights';
import {
  captureChartsForPdf,
  collectChartVisualizations,
} from './adminChartPdfCapture';
import { mapNebulaMessagesToChatMessages } from './mapNebulaMessagesToChatMessages';

function conversationPdfFilename(title: string): string {
  const cleaned = title
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
    .trim()
    .slice(0, 200);
  const base = cleaned || 'Untitled conversation';
  return base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`;
}

interface ConversationPdfExportProps {
  messages: NebulaMessage[];
  conversationTitle?: string;
  disabled?: boolean;
}

const ConversationPdfExport: React.FC<ConversationPdfExportProps> = ({
  messages,
  conversationTitle = 'Nebula Conversation',
  disabled = false,
}) => {
  const [currentViz, setCurrentViz] = useState<VisualizationData | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const renderContainerRef = useRef<HTMLDivElement>(null);
  const chatMessages = useMemo(() => mapNebulaMessagesToChatMessages(messages), [messages]);

  const { exportPdf, progress, isExporting, error, setRenderContainer } = usePdfExport();

  const handleRenderRef = useCallback(
    (el: HTMLDivElement | null) => {
      renderContainerRef.current = el;
      setRenderContainer(el);
    },
    [setRenderContainer]
  );

  const setVisualization = useCallback((viz: VisualizationData | null) => {
    flushSync(() => setCurrentViz(viz));
  }, []);

  const handleExport = async () => {
    const chartVizs = collectChartVisualizations(chatMessages);

    try {
      setIsCapturing(true);
      setStatusMessage(
        chartVizs.length > 0 ? 'Preparing charts for PDF...' : 'Preparing export...'
      );

      const chartImages = await captureChartsForPdf(
        chartVizs,
        () => renderContainerRef.current,
        setVisualization,
        (current, total) => {
          setStatusMessage(`Capturing chart ${current} of ${total}...`);
        }
      );

      setStatusMessage('Generating PDF...');
      await exportPdf(
        chatMessages,
        conversationTitle,
        { downloadFilename: conversationPdfFilename(conversationTitle) },
        chartImages
      );
    } finally {
      setIsCapturing(false);
      setStatusMessage(null);
      setCurrentViz(null);
    }
  };

  const isBusy = isCapturing || isExporting;
  const isDisabled = disabled || chatMessages.length === 0 || isBusy;
  const buttonLabel = statusMessage || progress?.message || (isBusy ? 'Exporting...' : 'Export PDF');

  const progressIcon = (() => {
    if (!isBusy && !progress) return <Download size={16} />;
    if (progress?.status === 'complete') return <Check size={16} className="text-emerald-600" />;
    if (progress?.status === 'error') return <AlertCircle size={16} className="text-red-500" />;
    return <Loader2 size={16} className="animate-spin" />;
  })();

  const progressTotal = progress?.total || chatMessages.length;
  const progressCurrent = progress?.current || 0;
  const showProgressBar = isBusy && progressTotal > 0;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !isDisabled && handleExport()}
        disabled={isDisabled}
        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-family-messina-sans transition-colors ${
          isDisabled
            ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900'
        }`}
        title={
          chatMessages.length === 0
            ? 'No messages to export'
            : isBusy
              ? buttonLabel
              : 'Export conversation as PDF'
        }
      >
        {progressIcon}
        <span className="hidden sm:inline max-w-[180px] truncate">{buttonLabel}</span>
      </button>

      {showProgressBar && (
        <div className="absolute -bottom-1 left-0 right-0 h-1 overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full bg-gray-800 transition-all duration-300"
            style={{ width: `${Math.min((progressCurrent / progressTotal) * 100, 100)}%` }}
          />
        </div>
      )}

      {error && (
        <div className="absolute right-0 top-full z-50 mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 font-family-messina-sans whitespace-nowrap">
          {error}
        </div>
      )}

      {createPortal(
        <PdfRenderer ref={handleRenderRef} visualization={currentViz} />,
        document.body
      )}
    </div>
  );
};

export default ConversationPdfExport;
